import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ClipboardCheck,
  RefreshCw,
  Printer,
  TriangleAlert,
  Users,
} from 'lucide-react';
import { useCurrentUser } from '../../hooks/useAuth';
import { usePeopleList } from '../../hooks/usePeople';
import { Button } from '../../components/ui/Button';
import { SkeletonList } from '../../components/ui/Skeleton';
import { EmptyState } from '../../components/ui/EmptyState';
import { formatDateTime, formatTime } from '../../lib/dateFmt';

/**
 * H&S register view (CLAUDE.md 6.2 — called "roll call" in the spec;
 * UK English uses "register" for the same thing). One-tap list of
 * everyone currently on site, printable, with an evacuation-mode
 * toggle that strips the page back to just the list at maximum
 * legibility.
 */
export function Register() {
  const { data: user, isLoading: meLoading } = useCurrentUser();
  const list = usePeopleList('', true);
  const [evacuation, setEvacuation] = useState(false);

  // Evacuation mode paints the entire viewport white via a global
  // attribute hook (see tokens.css `html[data-evacuation='true']`),
  // which overrides --bg / --surface-* to white. Without this the
  // cream Aurora floor leaks in below the <main> because AppShell
  // clamps the main's min-height to 0 inside its bounded scroll
  // container, exposing the floor.
  useEffect(() => {
    if (!evacuation) return;
    document.documentElement.dataset.evacuation = 'true';
    return () => {
      delete document.documentElement.dataset.evacuation;
    };
  }, [evacuation]);

  if (meLoading) return <main className="p-6 text-sm text-fg-muted">Loading...</main>;
  if (!user) {
    return (
      <main className="p-6 text-sm text-fg-muted">
        Please <Link to="/login" className="underline">sign in</Link>.
      </main>
    );
  }
  if (user.roles.length === 0) {
    return (
      <main className="p-6 text-sm text-fg-muted">
        Your account has no permissions yet. Ask an admin.
      </main>
    );
  }

  // H&S evacuation list = arrived AND not left-early. Anyone who's
  // recorded as left early is no longer in the building, so we
  // must not count them.
  const onSite = (list.data ?? []).filter((p) => !p.left_early);
  const stamped = formatDateTime(new Date());

  // Evacuation mode deliberately keeps literal white+black for maximum
  // legibility under stress — it isn't styled with theme tokens. Print
  // styles likewise force white background; both honoured here.
  return (
    <main
      className={
        evacuation
          ? 'min-h-screen bg-white p-4 text-2xl text-black sm:p-6'
          : 'min-h-screen bg-bg p-4 text-fg sm:p-6'
      }
    >
      <div className="mx-auto max-w-3xl">
        <header className="flex flex-wrap items-start justify-between gap-3 print:hidden">
          <div>
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-danger-soft-fg">
              <ClipboardCheck className="h-3.5 w-3.5" />
              <span>H&amp;S</span>
            </div>
            <h1 className="mt-1 text-2xl font-semibold tracking-tight text-fg sm:text-3xl">
              Register
            </h1>
            <p className="mt-1 text-sm text-fg-muted">
              <strong className="tabular-nums">{onSite.length}</strong> on site
              · as of {stamped}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="secondary"
              size="md"
              iconLeft={<RefreshCw className="h-4 w-4" />}
              onClick={() => list.refetch()}
              loading={list.isFetching}
            >
              Refresh
            </Button>
            <Button
              variant="primary"
              size="md"
              iconLeft={<Printer className="h-4 w-4" />}
              onClick={() => window.print()}
            >
              Print
            </Button>
            <Button
              variant={evacuation ? 'destructive' : 'destructive'}
              size="md"
              iconLeft={<TriangleAlert className="h-4 w-4" />}
              onClick={() => setEvacuation((v) => !v)}
            >
              {evacuation ? 'Exit evacuation' : 'Evacuation mode'}
            </Button>
          </div>
        </header>

        {/* Print-only header — keeps literal black borders for crisp
            output regardless of theme. */}
        <div className="mt-3 hidden border-y-2 border-black py-2 text-center text-base font-bold uppercase tracking-wider print:block">
          Register · {stamped}
        </div>

        {evacuation ? (
          <div className="mt-4 flex items-center justify-center gap-2 rounded-token-sm bg-danger px-4 py-3 text-center text-lg font-bold uppercase tracking-wider text-danger-fg shadow-token print:hidden">
            <TriangleAlert className="h-5 w-5" />
            Evacuation mode active
          </div>
        ) : null}

        <div
          className={
            evacuation
              ? 'mt-4 overflow-hidden rounded-lg border-2 border-black bg-white shadow'
              : 'mt-4 overflow-hidden rounded-token-lg border border-line/10 bg-surface shadow-token-sm'
          }
        >
          {list.isLoading ? (
            <SkeletonList rows={6} />
          ) : onSite.length === 0 ? (
            <EmptyState
              icon={<Users className="h-6 w-6" />}
              title="Nobody on site yet"
              description="Once reception checks people in they'll appear here in real time."
            />
          ) : (
            <ol className={evacuation ? 'divide-y divide-slate-200' : 'divide-y divide-divider/10'}>
              {onSite.map((p, idx) => (
                <li
                  key={p.id}
                  className={
                    evacuation
                      ? 'flex items-baseline gap-4 px-4 py-3 text-2xl'
                      : 'flex items-baseline gap-4 px-4 py-2.5 text-sm'
                  }
                >
                  <span
                    className={
                      evacuation
                        ? 'w-10 shrink-0 text-right tabular-nums font-semibold text-slate-500'
                        : 'w-8 shrink-0 text-right tabular-nums text-fg-dim'
                    }
                  >
                    {idx + 1}.
                  </span>
                  <span className={evacuation ? 'grow font-semibold' : 'grow font-semibold text-fg'}>{p.full_name}</span>
                  <span
                    className={
                      evacuation
                        ? 'shrink-0 text-base text-slate-700'
                        : 'shrink-0 text-xs text-fg-muted'
                    }
                  >
                    {p.position ?? '-'}
                  </span>
                  <span
                    className={
                      evacuation
                        ? 'shrink-0 tabular-nums text-base text-slate-700'
                        : 'shrink-0 tabular-nums text-xs text-fg-muted'
                    }
                  >
                    {p.arrived_at
                      ? formatTime(p.arrived_at)
                      : ''}
                  </span>
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>
    </main>
  );
}
